import React, { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCameraRetro,
  FaExclamationTriangle,
  FaImages,
} from "react-icons/fa";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiImage,
  FiMaximize2,
  FiX,
} from "react-icons/fi";

import { getAllGalleries } from "../../services/gallerieServices";
import { useUser } from "../Home/UserContext";
import Reveal from "../../components/Reveal/Reveal";
import { toAbsolutePhotoUrl as toAbsPhoto } from "../../services/albumPhotoServices";

const FALLBACK = "/img/no-image.jpg";
const MAX_HOME_ALBUMS = 3;
const MAX_PREVIEW_PHOTOS = 12;

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function sanitizeHtml(html = "") {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    FORBID_ATTR: ["style"],
  });
}

function stripHtml(html = "") {
  if (typeof window === "undefined") {
    return String(html || "")
      .replace(/<[^>]*>/g, "")
      .trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
}

function isActive(value) {
  if (value === undefined || value === null || value === "") {
    return true;
  }

  return value === true || value === 1 || value === "1";
}

function resolvePhoto(url) {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) {
    return FALLBACK;
  }

  return toAbsPhoto(cleanUrl) || FALLBACK;
}

function handleImageError(event) {
  if (!event?.currentTarget) return;

  event.currentTarget.onerror = null;
  event.currentTarget.src = FALLBACK;
}

function GallerySkeletonCard() {
  return (
    <div
      className="
        mx-auto w-full max-w-[390px] overflow-hidden
        rounded-[2rem] border border-slate-200
        bg-white shadow-sm
        dark:border-white/10 dark:bg-slate-900
      "
    >
      <div className="h-64 animate-pulse bg-slate-200 dark:bg-slate-800" />

      <div className="space-y-4 p-5">
        <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="
        col-span-full rounded-[2rem]
        border border-dashed border-slate-300
        bg-white/70 p-10 text-center shadow-sm
        dark:border-white/10 dark:bg-white/5
      "
    >
      <div
        className="
          mx-auto mb-4 flex h-14 w-14
          items-center justify-center rounded-2xl
          bg-orange-100 text-orange-600
          dark:bg-orange-500/10 dark:text-orange-400
        "
      >
        <FaImages />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucun album photo disponible
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-300">
        Les albums du club apparaîtront ici dès qu’ils seront publiés dans
        l’administration.
      </p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div
      className="
        col-span-full rounded-[1.5rem]
        border border-red-200 bg-red-50 p-4
        text-sm font-semibold text-red-700
        dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300
      "
    >
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
}

function GalleryCard({
  gallery,
  photos,
  cover,
  index,
  user,
  onPreview,
  onOpenAlbum,
}) {
  const descriptionText = stripHtml(gallery?.description || "");
  const previewPhotos = photos.slice(0, 4);

  return (
    <Reveal
      variant="zoom"
      delay={index * 90}
      className="mx-auto h-full w-full max-w-[390px]"
    >
      <article
        className="
          group relative mx-auto flex h-full w-full flex-col
          overflow-hidden rounded-[2rem]
          border border-slate-200 bg-white shadow-sm
          transition duration-300
          hover:-translate-y-2 hover:border-orange-300 hover:shadow-2xl
          dark:border-white/10 dark:bg-slate-900
          dark:hover:border-orange-500/40
        "
      >
        <button
          type="button"
          onClick={() => onPreview(gallery)}
          className="
            relative block h-72 w-full
            overflow-hidden text-left
            focus:outline-none focus:ring-4
            focus:ring-inset focus:ring-orange-500/30
          "
          aria-label={`Prévisualiser l'album ${gallery?.titre || ""}`}
          title="Prévisualiser l’album"
        >
          <img
            src={cover}
            alt={gallery?.titre || "Album photo"}
            loading="lazy"
            decoding="async"
            className="
              h-full w-full object-cover
              transition duration-700
              group-hover:scale-110
            "
            onError={handleImageError}
          />

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />

          <div
            className="
              pointer-events-none absolute inset-0
              bg-gradient-to-br
              from-transparent via-transparent to-orange-500/30
              opacity-0 transition duration-500
              group-hover:opacity-100
            "
          />

          <div
            className="
              absolute left-4 top-4 z-20
              inline-flex items-center gap-2
              rounded-full border border-white/30
              bg-white/95 px-4 py-2
              text-xs font-extrabold text-slate-950
              shadow-xl backdrop-blur-xl
            "
          >
            <FiImage className="text-orange-600" />

            {photos.length} photo{photos.length > 1 ? "s" : ""}
          </div>

          <div
            className="
              absolute right-4 top-4 z-20
              flex h-12 w-12 items-center justify-center
              rounded-full border border-white/40
              bg-white/95 text-slate-950
              shadow-2xl backdrop-blur-xl
              transition duration-200
              group-hover:border-orange-400
              group-hover:bg-orange-600
              group-hover:text-white
            "
          >
            <FiEye className="text-lg" />
          </div>

          <div className="pointer-events-none absolute bottom-0 left-0 right-0 p-5">
            <h3 className="font-barlow text-2xl font-extrabold leading-tight text-white drop-shadow-lg">
              {gallery?.titre || "Album du club"}
            </h3>
          </div>
        </button>

        <div className="flex flex-1 flex-col p-5 pb-7">
          {descriptionText ? (
            <p className="line-clamp-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {descriptionText}
            </p>
          ) : (
            <p className="text-sm italic leading-7 text-slate-500 dark:text-slate-400">
              Revivez les moments forts du club à travers cet album.
            </p>
          )}

          {previewPhotos.length > 1 && (
            <div className="mt-5 grid grid-cols-4 gap-2">
              {previewPhotos.map((photo, photoIndex) => (
                <button
                  key={photo?.id || `${gallery?.id}-${photoIndex}`}
                  type="button"
                  onClick={() => onPreview(gallery, photoIndex)}
                  className="
                    relative h-16 overflow-hidden rounded-2xl
                    border border-slate-200 bg-slate-100
                    transition duration-200
                    hover:-translate-y-0.5
                    hover:border-orange-300 hover:shadow-md
                    focus:outline-none focus:ring-4 focus:ring-orange-500/20
                    dark:border-white/10 dark:bg-slate-800
                    dark:hover:border-orange-500/40
                  "
                  aria-label={`Ouvrir la photo ${photoIndex + 1}`}
                >
                  <img
                    src={resolvePhoto(photo?.photo_url)}
                    alt={photo?.titre || gallery?.titre || "Photo"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onError={handleImageError}
                  />
                </button>
              ))}
            </div>
          )}

          <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
            <button
              type="button"
              onClick={() => onPreview(gallery)}
              className="
                inline-flex items-center justify-center gap-2
                rounded-full border border-orange-300
                bg-orange-50 px-5 py-3
                text-sm font-bold text-orange-700
                shadow-sm transition duration-200
                hover:-translate-y-0.5 hover:gap-3
                hover:border-orange-600
                hover:bg-orange-600 hover:text-white
                hover:shadow-lg
                focus:outline-none focus:ring-4 focus:ring-orange-500/20

                dark:border-orange-500/40
                dark:bg-orange-500/10
                dark:text-orange-300
                dark:hover:border-orange-400
                dark:hover:bg-orange-500
                dark:hover:text-white
              "
            >
              Prévisualiser
              <FiMaximize2 className="text-sm" />
            </button>

            {user && (
              <button
                type="button"
                onClick={() => onOpenAlbum(gallery.id)}
                className="
                  inline-flex items-center justify-center gap-2
                  rounded-full border border-orange-600
                  bg-orange-600 px-5 py-3
                  text-sm font-bold text-white
                  shadow-lg transition duration-200
                  hover:-translate-y-0.5 hover:gap-3
                  hover:border-orange-700 hover:bg-orange-700
                  hover:shadow-xl
                  focus:outline-none focus:ring-4 focus:ring-orange-500/25

                  dark:border-orange-500 dark:bg-orange-500
                  dark:hover:border-orange-400 dark:hover:bg-orange-400
                "
              >
                Voir l’album
                <FaArrowRight className="text-xs" />
              </button>
            )}
          </div>
        </div>

        <div
          aria-hidden="true"
          className="
            absolute bottom-0 left-0 right-0 h-1.5
            bg-gradient-to-r
            from-transparent via-orange-500 to-transparent
            opacity-80 transition
            group-hover:opacity-100
          "
        />
      </article>
    </Reveal>
  );
}

function AlbumPreviewModal({
  gallery,
  photos,
  user,
  onClose,
  onOpenAlbum,
  onOpenLightbox,
}) {
  const hasMorePhotos = photos.length > MAX_PREVIEW_PHOTOS;
  const previewPhotos = photos.slice(0, MAX_PREVIEW_PHOTOS);
  const safeDescription = sanitizeHtml(gallery?.description || "");

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  if (!gallery) return null;

  return (
    <div
      className="
        fixed inset-0 z-[80]
        flex items-center justify-center
        bg-slate-950/75 px-3 py-4
        backdrop-blur-sm
        sm:px-6 sm:py-8
      "
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="album-preview-title"
    >
      <div
        className="
          relative flex max-h-[92vh]
          w-full max-w-6xl flex-col
          overflow-hidden rounded-[2rem]
          border border-slate-200 bg-white
          shadow-2xl
          dark:border-white/10 dark:bg-slate-950
        "
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="
            sticky top-0 z-20
            border-b border-slate-200
            bg-white/95 p-4 backdrop-blur-xl
            dark:border-white/10 dark:bg-slate-950/95
            sm:p-6
          "
        >
          <button
            type="button"
            onClick={onClose}
            className="
              absolute right-4 top-4
              flex h-11 w-11 items-center justify-center
              rounded-full border border-slate-200
              bg-white text-slate-700
              shadow-lg transition duration-200
              hover:rotate-90 hover:border-orange-300
              hover:bg-orange-50 hover:text-orange-600
              focus:outline-none focus:ring-4 focus:ring-orange-500/20

              dark:border-white/10 dark:bg-slate-900
              dark:text-slate-200
              dark:hover:border-orange-500/40
              dark:hover:bg-orange-500/10
              dark:hover:text-orange-400
            "
            aria-label="Fermer l’album"
          >
            <FiX className="text-xl" />
          </button>

          <div className="pr-14">
            <div
              className="
                inline-flex items-center gap-2
                rounded-full border border-orange-200
                bg-orange-50 px-4 py-2
                text-xs font-bold uppercase
                tracking-[0.18em] text-orange-700
                dark:border-orange-500/20
                dark:bg-orange-500/10
                dark:text-orange-300
              "
            >
              <FaCameraRetro />
              Prévisualisation
            </div>

            <h3
              id="album-preview-title"
              className="mt-4 font-barlow text-2xl font-extrabold text-slate-950 dark:text-white sm:text-3xl"
            >
              {gallery?.titre || "Album photo"}
            </h3>

            <p className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-300">
              {photos.length} photo{photos.length > 1 ? "s" : ""}
              {hasMorePhotos
                ? ` — ${MAX_PREVIEW_PHOTOS} affichées en aperçu`
                : ""}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {safeDescription && (
            <div
              className={cn(
                "mb-6 rounded-3xl border border-slate-200 bg-slate-50 p-4",
                "dark:border-white/10 dark:bg-white/5",
                "prose prose-slate max-w-none text-sm leading-7",
                "text-slate-600 dark:prose-invert dark:text-slate-300",
                "prose-p:my-3 prose-li:my-1",
                "prose-a:font-bold prose-a:text-orange-600",
                "hover:prose-a:text-orange-700",
                "dark:prose-a:text-orange-400",
                "dark:hover:prose-a:text-orange-300",
                "prose-strong:text-slate-950",
                "dark:prose-strong:!text-white",
                "prose-b:text-slate-950",
                "dark:prose-b:!text-white"
              )}
              dangerouslySetInnerHTML={{
                __html: safeDescription,
              }}
            />
          )}

          {previewPhotos.length === 0 ? (
            <div
              className="
                rounded-[2rem]
                border border-dashed border-slate-300
                bg-slate-50 p-10 text-center
                text-sm italic text-slate-500
                dark:border-white/10 dark:bg-white/5
                dark:text-slate-400
              "
            >
              Aucune photo dans cet album.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {previewPhotos.map((photo, index) => (
                <button
                  key={photo?.id || index}
                  type="button"
                  onClick={() => onOpenLightbox(index)}
                  className="
                    group relative h-36 overflow-hidden
                    rounded-2xl border border-slate-200
                    bg-slate-100 shadow-sm
                    transition duration-200
                    hover:-translate-y-1
                    hover:border-orange-300 hover:shadow-lg
                    focus:outline-none focus:ring-4 focus:ring-orange-500/20

                    dark:border-white/10 dark:bg-slate-800
                    dark:hover:border-orange-500/40
                    sm:h-44
                  "
                  aria-label={`Ouvrir la photo ${index + 1}`}
                >
                  <img
                    src={resolvePhoto(photo?.photo_url)}
                    alt={photo?.titre || gallery?.titre || "Photo"}
                    className="
                      h-full w-full object-cover
                      transition duration-500
                      group-hover:scale-105
                    "
                    loading="lazy"
                    decoding="async"
                    onError={handleImageError}
                  />

                  <div
                    className="
                      pointer-events-none absolute inset-0
                      bg-gradient-to-t
                      from-slate-950/60 via-transparent to-transparent
                      opacity-0 transition
                      group-hover:opacity-100
                    "
                  />

                  <div
                    className="
                      absolute right-3 top-3
                      flex h-9 w-9 items-center justify-center
                      rounded-full border border-white/30
                      bg-white/95 text-slate-950
                      opacity-0 shadow-lg backdrop-blur
                      transition duration-200
                      group-hover:bg-orange-600
                      group-hover:text-white
                      group-hover:opacity-100
                    "
                  >
                    <FiMaximize2 />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div
          className="
            border-t border-slate-200
            bg-white/95 p-4 backdrop-blur-xl
            dark:border-white/10 dark:bg-slate-950/95
            sm:p-5
          "
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
              Pour éviter les problèmes d’affichage sur mobile, la
              prévisualisation est volontairement limitée.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onClose}
                className="
                  inline-flex items-center justify-center
                  rounded-full border border-slate-200
                  bg-white px-5 py-3
                  text-sm font-bold text-slate-700
                  shadow-sm transition duration-200
                  hover:-translate-y-0.5
                  hover:border-orange-200
                  hover:bg-orange-50
                  hover:text-orange-700

                  dark:border-white/10
                  dark:bg-white/5
                  dark:text-slate-200
                  dark:hover:border-orange-500/30
                  dark:hover:bg-orange-500/10
                  dark:hover:text-orange-300
                "
              >
                Fermer
              </button>

              {user && (
                <button
                  type="button"
                  onClick={() => onOpenAlbum(gallery.id)}
                  className="
                    inline-flex items-center justify-center gap-2
                    rounded-full border border-orange-600
                    bg-orange-600 px-5 py-3
                    text-sm font-bold text-white
                    shadow-lg transition duration-200
                    hover:-translate-y-0.5 hover:gap-3
                    hover:border-orange-700 hover:bg-orange-700
                    hover:shadow-xl
                    focus:outline-none focus:ring-4 focus:ring-orange-500/25

                    dark:border-orange-500 dark:bg-orange-500
                    dark:hover:border-orange-400 dark:hover:bg-orange-400
                  "
                >
                  Voir toutes les photos
                  <FaArrowRight className="text-xs" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Lightbox({ gallery, photos, index, onClose, onPrev, onNext }) {
  const currentPhoto = photos[index];

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }

      if (event.key === "ArrowLeft") {
        onPrev();
      }

      if (event.key === "ArrowRight") {
        onNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, onPrev, onNext]);

  if (!currentPhoto) return null;

  return (
    <div
      className="
        fixed inset-0 z-[90]
        flex items-center justify-center
        bg-slate-950/90 p-3
        backdrop-blur-sm
      "
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Agrandissement de la photo"
    >
      <div
        className="relative w-full max-w-6xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="
            absolute -right-2 -top-2 z-20
            flex h-11 w-11 items-center justify-center
            rounded-full border border-white/20
            bg-white/95 text-slate-900
            shadow-xl transition duration-200
            hover:rotate-90 hover:border-orange-400
            hover:bg-orange-600 hover:text-white
            focus:outline-none focus:ring-4 focus:ring-orange-500/30
          "
          aria-label="Fermer"
        >
          <FiX className="text-xl" />
        </button>

        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={onPrev}
              className="
                absolute left-2 top-1/2 z-20
                flex h-11 w-11 -translate-y-1/2
                items-center justify-center
                rounded-full border border-white/20
                bg-white/95 text-slate-900
                shadow-xl transition duration-200
                hover:scale-105 hover:border-orange-400
                hover:bg-orange-600 hover:text-white
                focus:outline-none focus:ring-4 focus:ring-orange-500/30
                sm:left-4
              "
              aria-label="Photo précédente"
            >
              <FiChevronLeft className="text-xl" />
            </button>

            <button
              type="button"
              onClick={onNext}
              className="
                absolute right-2 top-1/2 z-20
                flex h-11 w-11 -translate-y-1/2
                items-center justify-center
                rounded-full border border-white/20
                bg-white/95 text-slate-900
                shadow-xl transition duration-200
                hover:scale-105 hover:border-orange-400
                hover:bg-orange-600 hover:text-white
                focus:outline-none focus:ring-4 focus:ring-orange-500/30
                sm:right-4
              "
              aria-label="Photo suivante"
            >
              <FiChevronRight className="text-xl" />
            </button>
          </>
        )}

        <img
          src={resolvePhoto(currentPhoto?.photo_url)}
          alt={currentPhoto?.titre || gallery?.titre || "Photo"}
          className="
            max-h-[82vh] w-full rounded-[2rem]
            border border-white/15 bg-black/20
            object-contain shadow-2xl
          "
          onError={handleImageError}
        />

        <div className="mt-4 flex items-center justify-center gap-3 text-sm font-bold text-white/90">
          <span
            className="
              rounded-full border border-white/15
              bg-white/10 px-3 py-1.5
              backdrop-blur
            "
          >
            {index + 1} / {photos.length}
          </span>

          {currentPhoto?.titre && (
            <span className="hidden text-white/70 sm:inline">
              — {currentPhoto.titre}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LastGallerie() {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();
  const { user } = useUser();

  const orderPhotos = useCallback((photos = []) => {
    const arr = Array.isArray(photos) ? [...photos] : [];

    arr.sort((a, b) => {
      const positionA = Number.isFinite(Number(a?.position))
        ? Number(a.position)
        : Number.MAX_SAFE_INTEGER;

      const positionB = Number.isFinite(Number(b?.position))
        ? Number(b.position)
        : Number.MAX_SAFE_INTEGER;

      return positionA - positionB || (a?.id ?? 0) - (b?.id ?? 0);
    });

    return arr.filter((photo) => isActive(photo?.is_active));
  }, []);

  const getCoverPhoto = useCallback(
    (gallery) => {
      const ordered = orderPhotos(gallery?.photos || []);

      return (
        ordered.find(
          (photo) =>
            photo?.is_cover === true ||
            photo?.is_cover === 1 ||
            photo?.is_cover === "1"
        ) ||
        ordered[0] ||
        null
      );
    },
    [orderPhotos]
  );

  useEffect(() => {
    let mounted = true;

    const fetchGalleries = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const all = await getAllGalleries();

        const active = (Array.isArray(all) ? all : [])
          .filter((gallery) => isActive(gallery?.is_active))
          .slice(0, MAX_HOME_ALBUMS);

        if (!mounted) return;

        setGalleries(active);
      } catch (error) {
        console.error("Failed to fetch galleries", error);

        if (mounted) {
          setGalleries([]);
          setErrorMsg("Impossible de charger les albums photos.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchGalleries();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedPhotos = useMemo(() => {
    return orderPhotos(selectedGallery?.photos || []);
  }, [selectedGallery, orderPhotos]);

  const previewLightboxPhotos = useMemo(() => {
    return selectedPhotos.slice(0, MAX_PREVIEW_PHOTOS);
  }, [selectedPhotos]);

  const openAlbumPreview = (gallery, startIndex = 0) => {
    const orderedPhotos = orderPhotos(gallery?.photos || []);
    const previewLength = Math.min(
      orderedPhotos.length,
      MAX_PREVIEW_PHOTOS
    );

    const safeStartIndex =
      previewLength > 0
        ? Math.min(Math.max(Number(startIndex) || 0, 0), previewLength - 1)
        : 0;

    setSelectedGallery(gallery);
    setLightboxIndex(safeStartIndex);
    setLightboxOpen(false);
  };

  const closeAlbumPreview = () => {
    setSelectedGallery(null);
    setLightboxOpen(false);
    setLightboxIndex(0);
  };

  const openLightbox = (index = 0) => {
    const total = previewLightboxPhotos.length;

    if (!total) return;

    const safeIndex = Math.min(
      Math.max(Number(index) || 0, 0),
      total - 1
    );

    setLightboxIndex(safeIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const prevPhoto = useCallback(() => {
    const total = previewLightboxPhotos.length;

    if (!total) return;

    setLightboxIndex(
      (currentIndex) => (currentIndex - 1 + total) % total
    );
  }, [previewLightboxPhotos.length]);

  const nextPhoto = useCallback(() => {
    const total = previewLightboxPhotos.length;

    if (!total) return;

    setLightboxIndex(
      (currentIndex) => (currentIndex + 1) % total
    );
  }, [previewLightboxPhotos.length]);

  const handleOpenAlbumPage = (galleryId) => {
    navigate(`/galerie-photos/album/${galleryId}`);
  };

  return (
    <section
      id="albums"
      className="
        relative scroll-mt-[calc(var(--site-navbar-h)+24px)]
        overflow-hidden bg-slate-50 py-20
        text-slate-950
        dark:bg-slate-950 dark:text-white
        lg:py-28
      "
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div
            className="
              inline-flex items-center gap-2
              rounded-full border border-orange-200
              bg-orange-50 px-4 py-2
              text-xs font-bold uppercase
              tracking-[0.22em] text-orange-700
              shadow-sm
              dark:border-orange-500/20
              dark:bg-orange-500/10
              dark:text-orange-300
            "
          >
            <FaCameraRetro />
            Galerie du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Derniers{" "}
            <span className="text-orange-600 dark:text-orange-400">
              albums photo
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Revivez les entraînements, compétitions, événements et moments forts
            du club.
          </p>
        </Reveal>

        <div
          className="
            mt-14 grid justify-center gap-6
            grid-cols-[repeat(auto-fit,minmax(280px,390px))]
          "
        >
          {loading &&
            Array.from({ length: 3 }).map((_, index) => (
              <GallerySkeletonCard
                key={`gallery-skeleton-${index}`}
              />
            ))}

          {!loading && errorMsg && (
            <ErrorState message={errorMsg} />
          )}

          {!loading && !errorMsg && galleries.length === 0 && (
            <EmptyState />
          )}

          {!loading &&
            !errorMsg &&
            galleries.map((gallery, index) => {
              const photos = orderPhotos(gallery?.photos || []);
              const cover = getCoverPhoto(gallery);

              const coverSrc = cover?.photo_url
                ? resolvePhoto(cover.photo_url)
                : FALLBACK;

              return (
                <GalleryCard
                  key={gallery.id || index}
                  gallery={gallery}
                  photos={photos}
                  cover={coverSrc}
                  index={index}
                  user={user}
                  onPreview={openAlbumPreview}
                  onOpenAlbum={handleOpenAlbumPage}
                />
              );
            })}
        </div>
      </div>

      {selectedGallery && (
        <AlbumPreviewModal
          gallery={selectedGallery}
          photos={selectedPhotos}
          user={user}
          onClose={closeAlbumPreview}
          onOpenAlbum={handleOpenAlbumPage}
          onOpenLightbox={openLightbox}
        />
      )}

      {selectedGallery &&
        lightboxOpen &&
        previewLightboxPhotos.length > 0 && (
          <Lightbox
            gallery={selectedGallery}
            photos={previewLightboxPhotos}
            index={lightboxIndex}
            onClose={closeLightbox}
            onPrev={prevPhoto}
            onNext={nextPhoto}
          />
        )}
    </section>
  );
}